/* eslint-disable @typescript-eslint/no-explicit-any */
import { useServices } from "@/services/hooks/useServices";
import {
  B2cSender,
  BulkB2cSenderRequest,
  CreateB2cSenderDto,
  FilterB2cSenderDto,
  ManageSeedEspTypesDto,
  UpdateB2cSenderDto,
} from "@/types/b2c-sender.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SUBSCRIPTION_QUERY_KEY } from "./use-subscription";

/**
 * Get paginated list of B2C senders with optional filters
 */
export const useB2cSenders = (filters?: FilterB2cSenderDto) => {
  const { b2cSenderService } = useServices();

  return useQuery({
    queryKey: ["b2cSenders", filters],
    queryFn: () => b2cSenderService.getSenders(filters),
  });
};

/**
 * Get a single B2C sender by ID
 */
export const useB2cSender = (id: string, enabled = true) => {
  const { b2cSenderService } = useServices();

  return useQuery({
    queryKey: ["b2cSender", id],
    queryFn: () => b2cSenderService.getSenderById(id),
    enabled: enabled && !!id,
  });
};

/**
 * Create a single B2C sender
 */
export const useCreateB2cSender = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (senderData: CreateB2cSenderDto) =>
      b2cSenderService.createSender(senderData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      toast.success("B2C sender created successfully");
    },
    onError: () => {
      toast.error("Failed to create B2C sender");
    },
  });
};

/**
 * Create multiple B2C senders in bulk
 */
export const useCreateBulkB2cSenders = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BulkB2cSenderRequest) =>
      b2cSenderService.createBulkSenders(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      if (data.totalFailed === 0) {
        toast.success(`Successfully created ${data.totalCreated} senders`);
      } else {
        toast.warning(
          `Created ${data.totalCreated} senders, failed: ${data.totalFailed}`
        );
      }
    },
    onError: () => {
      toast.error("Failed to create bulk senders");
    },
  });
};

/**
 * Update a single B2C sender
 */
export const useUpdateB2cSender = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateB2cSenderDto }) =>
      b2cSenderService.updateSender(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
      toast.success("B2C sender updated successfully");
    },
    onError: () => {
      toast.error("Failed to update B2C sender");
    },
  });
};

/**
 * Bulk update multiple B2C senders
 */
export const useBulkUpdateB2cSenders = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ids,
      updates,
    }: {
      ids: string[];
      updates: UpdateB2cSenderDto;
    }) => b2cSenderService.bulkUpdateSenders({ ids, updates }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      toast.success("B2C senders updated successfully");
    },
    onError: () => {
      toast.error("Failed to update B2C senders");
    },
  });
};

/**
 * Delete a single B2C sender
 */
export const useDeleteB2cSender = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => b2cSenderService.deleteSender(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      toast.success("B2C sender deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete B2C sender");
    },
  });
};

/**
 * Export B2C senders as CSV
 */
export const useExportB2cSenders = () => {
  const { b2cSenderService } = useServices();

  return useMutation({
    mutationFn: () => b2cSenderService.exportSendersAsCsv(),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "b2c-senders.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("B2C senders exported successfully");
    },
    onError: () => {
      toast.error("Failed to export B2C senders");
    },
  });
};

/**
 * Toggle warmup for a single sender
 */
export const useToggleB2cSenderWarmup = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled, email }: { id: string; enabled: boolean; email: string }) =>
      b2cSenderService.updateSender(id, { isWarmupEnabled: enabled, email: email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      toast.success("Warmup status updated successfully");
    },
    onError: () => {
      toast.error("Failed to update warmup status");
    },
  });
};

/**
 * Bulk toggle warmup for multiple senders
 */
export const useBulkToggleB2cSenderWarmup = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, enabled }: { ids: string[]; enabled: boolean }) =>
      b2cSenderService.bulkUpdateSenders({
        ids,
        updates: { isWarmupEnabled: enabled },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      toast.success("Warmup status updated successfully");
    },
    onError: () => {
      toast.error("Failed to update warmup status");
    },
  });
};

/**
 * Manage seed ESP types for a sender
 */
export const useManageSeedEspTypes = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ManageSeedEspTypesDto }) =>
      b2cSenderService.manageSeedEspTypes(id, dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["mailbox", variables.id] });
      toast.success("Seed ESP types updated successfully");
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || "Failed to update seed ESP types";
      toast.error(errorMessage);
    },
  });
};

/**
 * Manage seed ESP types for a B2C sender
 */
// export const useManageSeedEspTypes = () => {
//   const { b2cSenderService } = useServices();
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: ({ id, seedEspTypes }: { id: string; seedEspTypes: string[] }) =>
//       b2cSenderService.manageSeedEspTypes(id, { 
//         actions: seedEspTypes.map(espType => ({ espType, operation: "add" as const })),
//         seedEspTypes 
//       }),
//     onSuccess: (_, variables) => {
//       queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
//       queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
//       queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
//       toast.success("Seed ESP types updated successfully");
//     },
//     onError: () => {
//       toast.error("Failed to update seed ESP types");
//     },
//   });
// };

/**
 * Assign a plan to a B2C sender profile
 */
export const useAssignSenderPlan = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      planId,
      subscriptionId,
    }: {
      id: string;
      planId: string;
      subscriptionId: string;
    }) => b2cSenderService.assignPlan(id, { planId, subscriptionId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"] });
    },
  });
};

/**
 * Unassign a plan from a B2C sender profile
 */
export const useUnassignSenderPlan = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => b2cSenderService.unassignPlan(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", id] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY, "active-subscription-plans"] });
    },
  });
};

/**
 * Assign a plan to a B2C sender
 */
export const useAssignB2cSenderPlan = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      planId,
      subscriptionId,
    }: {
      id: string;
      planId: string;
      subscriptionId: string;
    }) =>
      b2cSenderService.assignPlan(id, { planId, subscriptionId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      toast.success("Plan assigned successfully");
    },
    onError: () => {
      toast.error("Failed to assign plan");
    },
  });
};

/**
 * Unassign a plan from a B2C sender
 */
export const useUnassignB2cSenderPlan = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => b2cSenderService.unassignPlan(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", id] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      toast.success("Plan unassigned successfully");
    },
    onError: () => {
      toast.error("Failed to unassign plan");
    },
  });
};

export const useCreateSenderReminders = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { emails: string[]; remindAt: string; phoneNumber: string }) =>
      b2cSenderService.createReminders(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["senderReminders"] });
      toast.success("Reminders scheduled successfully");
    },
    onError: () => {
      toast.error("Failed to schedule reminders");
    },
  });
};

export const useGetSenderReminders = (enabled = true) => {
  const { b2cSenderService } = useServices();
  return useQuery({
    queryKey: ["senderReminders"],
    queryFn: () => b2cSenderService.getReminders(),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useDeleteSenderReminders = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => b2cSenderService.deleteReminders(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["senderReminders"] });
      toast.success("Reminder deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete reminder");
    },
  });
};

/** GET /b2c-sender/available-labels - options for assign dropdown and filter (SENDER_PROFILE labels only) */
export const useAvailableSenderLabels = () => {
  const { b2cSenderService } = useServices();
  return useQuery({
    queryKey: ["b2cSender", "available-labels"],
    queryFn: () => b2cSenderService.getAvailableLabels(),
  });
};

/** POST /b2c-sender/:id/labels - assign labels to a sender */
export const useAddSenderLabels = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, labelIds }: { id: string; labelIds: string[] }) =>
      b2cSenderService.addLabels(id, { labelIds }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", "available-labels"] });
      toast.success("Labels assigned");
    },
    onError: () => toast.error("Failed to assign labels"),
  });
};

/** DELETE /b2c-sender/:id/labels - unassign labels from a sender */
export const useRemoveSenderLabels = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, labelIds }: { id: string; labelIds: string[] }) =>
      b2cSenderService.removeLabels(id, { labelIds }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", "available-labels"] });
      toast.success("Labels removed");
    },
    onError: () => toast.error("Failed to remove labels"),
  });
};

/** POST /b2c-sender/bulk-add-labels */
export const useBulkAddSenderLabels = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { senderIds: string[]; labelIds: string[] }) =>
      b2cSenderService.bulkAddLabels(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", "available-labels"] });
      toast.success("Labels added to selected senders");
    },
    onError: () => toast.error("Failed to add labels"),
  });
};

/** POST /b2c-sender/bulk-remove-labels */
export const useBulkRemoveSenderLabels = () => {
  const { b2cSenderService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { senderIds: string[]; labelIds: string[] }) =>
      b2cSenderService.bulkRemoveLabels(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender", "available-labels"] });
      toast.success("Labels removed from selected senders");
    },
    onError: () => toast.error("Failed to remove labels"),
  });
};
